// src/services/businessActivitiesApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export interface AppActivity {
  id: string
  businessId: string
  userId: string
  userName?: string
  userEmail?: string
  type: ActivityType
  title: string
  description?: string
  department?: string
  team?: string
  projectId?: string
  projectName?: string
  status: ActivityStatus
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  duration?: number // in minutes
  startTime?: string
  endTime?: string
  location?: {
    type: 'office' | 'remote' | 'client_site' | 'other'
    address?: string
    coordinates?: {
      lat: number
      lng: number
    }
  }
  attachments?: {
    id: string
    name: string
    type: string
    size: number
    url: string
  }[]
  metadata: {
    deviceInfo?: {
      device_type?: string
      device_model?: string
      os_version?: string
      app_version?: string
    }
    ipAddress?: string
    userAgent?: string
    source?: 'mobile_app' | 'web_app' | 'api' | 'system'
    billable?: boolean
    invoiced?: boolean
    [key: string]: any
  }
  tags: string[]
  relatedActivities?: string[] // IDs of related activities
  parentActivityId?: string
  isPersonalData: boolean
  createdAt: string
  updatedAt: string
}

export type ActivityType = 
  | 'timesheet_entry'
  | 'timesheet_edit'
  | 'timesheet_delete'
  | 'task_created'
  | 'task_updated'
  | 'task_completed'
  | 'task_assigned'
  | 'client_visit'
  | 'client_call'
  | 'client_meeting'
  | 'photo_upload'
  | 'document_upload'
  | 'document_view'
  | 'report_generated'
  | 'invoice_created'
  | 'invoice_sent'
  | 'payment_received'
  | 'project_created'
  | 'project_updated'
  | 'project_milestone'
  | 'team_meeting'
  | 'training_session'
  | 'break_start'
  | 'break_end'
  | 'shift_start'
  | 'shift_end'
  | 'expense_entry'
  | 'travel_log'
  | 'other'

export type ActivityStatus = 
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'on_hold'
  | 'review_required'
  | 'approved'
  | 'rejected'

export interface ActivitiesResponse {
  status: 'success' | 'error'
  message: string
  data: {
    activities: AppActivity[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
      hasNextPage: boolean
      hasPrevPage: boolean
    }
  }
}

export interface ActivitySummaryResponse {
  status: 'success' | 'error'
  message: string
  data: {
    period: string
    totalActivities: number
    activeUsers: number
    activityBreakdown: {
      type: string
      count: number
    }[]
    teamPerformance: {
      name: string
      value: number
    }[]
    mostActiveType?: string
  }
}

export interface RecentActivitiesResponse {
  status: 'success' | 'error'
  message: string
  data: {
    activities: AppActivity[]
    total: number
  }
}

export interface UserActivityResponse {
  status: 'success' | 'error'
  message: string
  data: {
    activities: AppActivity[]
    summary: {
      totalActivities: number
      timesheetActivities: number
      taskActivities: number
      clientActivities: number
      mediaActivities: number
    }
    period: string
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }
}

export interface ActivitiesByTypeResponse {
  status: 'success' | 'error'
  message: string
  data: {
    period: string
    breakdown: {
      type: ActivityType
      count: number
      activities: AppActivity[]
    }[]
    totalActivities: number
  }
}

export interface LiveActivityResponse {
  status: 'success' | 'error'
  message: string
  data: {
    activities: AppActivity[]
    total: number
    lastUpdated: string
    period: string
  }
}

export interface ActivityFilters {
  businessId: string
  userId?: string
  type?: ActivityType
  department?: string
  team?: string
  projectId?: string
  status?: ActivityStatus
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
}

export const businessActivitiesApi = createApi({
  reducerPath: 'businessActivitiesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/activities`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['Activities', 'ActivitySummary', 'UserActivity'],
  endpoints: (builder) => ({
    // Get activities with filters
    getActivities: builder.query<ActivitiesResponse, ActivityFilters>({
      query: (params) => ({
        url: '',
        params: {
          businessId: params.businessId,
          userId: params.userId,
          type: params.type,
          department: params.department,
          team: params.team,
          projectId: params.projectId,
          status: params.status,
          startDate: params.startDate,
          endDate: params.endDate,
          page: params.page || 1,
          limit: params.limit || 25
        }
      }),
      providesTags: ['Activities']
    }),

    // Get activity summary for dashboard
    getActivitySummary: builder.query<ActivitySummaryResponse, { 
      businessId: string; 
      days?: number 
    }>({
      query: ({ businessId, days = 7 }) => ({
        url: '/summary',
        params: { businessId, days }
      }),
      providesTags: ['ActivitySummary']
    }),

    // Get recent activities
    getRecentActivities: builder.query<RecentActivitiesResponse, { 
      businessId: string; 
      limit?: number 
    }>({
      query: ({ businessId, limit = 20 }) => ({
        url: '/recent',
        params: { businessId, limit }
      }),
      providesTags: ['Activities']
    }),

    // Get activities for a specific user
    getUserActivity: builder.query<UserActivityResponse, {
      businessId: string;
      userId: string;
      days?: number;
      page?: number;
      limit?: number;
    }>({
      query: ({ businessId, userId, days = 30, page = 1, limit = 25 }) => ({
        url: '/user-activity',
        params: { businessId, userId, days, page, limit }
      }),
      providesTags: ['UserActivity']
    }),

    // Get activities grouped by type
    getActivitiesByType: builder.query<ActivitiesByTypeResponse, {
      businessId: string;
      days?: number;
    }>({
      query: ({ businessId, days = 7 }) => ({
        url: '/by-type',
        params: { businessId, days }
      }),
      providesTags: ['Activities']
    }),

    // Get live activity feed (last 24 hours)
    getLiveActivityFeed: builder.query<LiveActivityResponse, {
      businessId: string;
      limit?: number;
    }>({
      query: ({ businessId, limit = 50 }) => ({
        url: '/live',
        params: { businessId, limit }
      }),
      providesTags: ['Activities']
    })
  })
})

export const {
  useGetActivitiesQuery,
  useGetActivitySummaryQuery,
  useGetRecentActivitiesQuery,
  useGetUserActivityQuery,
  useGetActivitiesByTypeQuery,
  useGetLiveActivityFeedQuery
} = businessActivitiesApi